import { actionToId } from '../core/actions';
import type { ValidAction } from '../../multiplayer/types';
import type { AIPairedPlayConfidence } from './types';

export const PAIRED_CONFIDENCE_LEVEL = 0.95 as const;
export const PAIRED_CONFIDENCE_LOOK_COUNT = 4 as const;

interface PairedConfidenceEvaluation {
  action: ValidAction;
  contractUtility: number;
  contractUtilityOutcomes: readonly number[];
  worldWeights: readonly number[];
  worldSource: 'sampled' | 'complete-enumeration' | 'truncated-enumeration';
  simulationCount: number;
}

function compareStableIds(left: string, right: string): number {
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

function requireSharedWorldEvidence(evaluations: readonly PairedConfidenceEvaluation[]): number {
  const evaluatedWorldCount = evaluations[0]?.contractUtilityOutcomes.length ?? 0;
  if (evaluatedWorldCount === 0) {
    throw new Error('Paired confidence requires at least one evaluated world.');
  }

  for (const evaluation of evaluations) {
    if (
      evaluation.contractUtilityOutcomes.length !== evaluatedWorldCount ||
      evaluation.worldWeights.length !== evaluatedWorldCount ||
      evaluation.simulationCount !== evaluatedWorldCount ||
      evaluation.worldSource !== evaluations[0]!.worldSource
    ) {
      throw new Error('Paired confidence requires one shared ordered world set.');
    }
  }
  return evaluatedWorldCount;
}

function logGamma(value: number): number {
  const coefficients = [
    676.5203681218851,
    -1259.1392167224028,
    771.3234287776531,
    -176.6150291621406,
    12.507343278686905,
    -0.13857109526572012,
    9.984369578019572e-6,
    1.5056327351493116e-7,
  ];
  if (value < 0.5) {
    return Math.log(Math.PI) - Math.log(Math.sin(Math.PI * value)) - logGamma(1 - value);
  }
  const shifted = value - 1;
  let sum = 0.9999999999998099;
  for (let index = 0; index < coefficients.length; index += 1) {
    sum += coefficients[index]! / (shifted + index + 1);
  }
  const base = shifted + coefficients.length - 0.5;
  return 0.5 * Math.log(2 * Math.PI) + (shifted + 0.5) * Math.log(base) - base + Math.log(sum);
}

function betaContinuedFraction(a: number, b: number, x: number): number {
  const maxIterations = 200;
  const epsilon = 3e-14;
  const floor = 1e-300;
  const sum = a + b;
  const aPlusOne = a + 1;
  const aMinusOne = a - 1;
  let c = 1;
  let d = 1 - (sum * x) / aPlusOne;
  if (Math.abs(d) < floor) d = floor;
  d = 1 / d;
  let result = d;

  for (let iteration = 1; iteration <= maxIterations; iteration += 1) {
    const even = 2 * iteration;
    let term = (iteration * (b - iteration) * x) / ((aMinusOne + even) * (a + even));
    d = 1 + term * d;
    if (Math.abs(d) < floor) d = floor;
    c = 1 + term / c;
    if (Math.abs(c) < floor) c = floor;
    d = 1 / d;
    result *= d * c;

    term = (-(a + iteration) * (sum + iteration) * x) / ((a + even) * (aPlusOne + even));
    d = 1 + term * d;
    if (Math.abs(d) < floor) d = floor;
    c = 1 + term / c;
    if (Math.abs(c) < floor) c = floor;
    d = 1 / d;
    const delta = d * c;
    result *= delta;
    if (Math.abs(delta - 1) < epsilon) return result;
  }
  throw new Error('Paired confidence could not evaluate the Student t distribution.');
}

function regularizedBeta(x: number, a: number, b: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const factor = Math.exp(
    logGamma(a + b) - logGamma(a) - logGamma(b) + a * Math.log(x) + b * Math.log(1 - x),
  );
  if (x < (a + 1) / (a + b + 2)) {
    return (factor * betaContinuedFraction(a, b, x)) / a;
  }
  return 1 - (factor * betaContinuedFraction(b, a, 1 - x)) / b;
}

function studentTCdf(value: number, degreesOfFreedom: number): number {
  if (value === 0) return 0.5;
  const x = degreesOfFreedom / (degreesOfFreedom + value * value);
  const tail = regularizedBeta(x, degreesOfFreedom / 2, 0.5) / 2;
  return value > 0 ? 1 - tail : tail;
}

export function studentTQuantile(probability: number, degreesOfFreedom: number): number {
  if (!Number.isFinite(probability) || probability <= 0.5 || probability >= 1) {
    throw new Error('Student t probability must be greater than 0.5 and less than 1.');
  }
  if (!Number.isFinite(degreesOfFreedom) || degreesOfFreedom <= 0) {
    throw new Error('Student t degrees of freedom must be positive.');
  }

  let lower = 0;
  let upper = 1;
  while (studentTCdf(upper, degreesOfFreedom) < probability) {
    lower = upper;
    upper *= 2;
    if (!Number.isFinite(upper)) {
      throw new Error('Student t quantile exceeds the finite numeric range.');
    }
  }
  for (let iteration = 0; iteration < 100; iteration += 1) {
    const middle = (lower + upper) / 2;
    if (studentTCdf(middle, degreesOfFreedom) < probability) lower = middle;
    else upper = middle;
  }
  return (lower + upper) / 2;
}

function sampleStandardDeviation(values: readonly number[]): number {
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const squared = values.reduce((sum, value) => sum + (value - mean) ** 2, 0);
  return Math.sqrt(squared / (values.length - 1));
}

/**
 * Compare the current leader with all alternatives on paired world outcomes.
 *
 * Sampled worlds use a one-sided paired Student t interval. Equal alpha
 * spending across four planned looks and all ordered pairs keeps 95% family
 * confidence under iid sampled worlds and the paired-mean normal assumption.
 * Shared behavior weights remain coherent: the test uses w*(difference-band),
 * whose positive expectation is equivalent to a weighted advantage over the
 * strategic band. Exhaustive endgames use their exact weighted means.
 */
export function buildPairedPlayConfidence(
  evaluations: readonly PairedConfidenceEvaluation[],
  strategicTolerance: number,
): AIPairedPlayConfidence {
  if (evaluations.length < 2) {
    throw new Error('Paired confidence requires at least two candidates.');
  }
  if (!Number.isFinite(strategicTolerance) || strategicTolerance < 0) {
    throw new Error('Paired confidence requires a non-negative strategic tolerance.');
  }

  const evaluatedWorldCount = requireSharedWorldEvidence(evaluations);
  const ordered = [...evaluations].sort(
    (left, right) =>
      right.contractUtility - left.contractUtility ||
      compareStableIds(actionToId(left.action.action), actionToId(right.action.action)),
  );
  const leader = ordered[0]!;
  const leaderActionId = actionToId(leader.action.action);
  const exact = leader.worldSource === 'complete-enumeration';
  const truncated = leader.worldSource === 'truncated-enumeration';
  if (!exact && !truncated && evaluatedWorldCount < 2) {
    throw new Error('Sampled paired confidence requires at least two worlds.');
  }
  const orderedPairCount = evaluations.length * (evaluations.length - 1);
  const perComparisonAlpha =
    (1 - PAIRED_CONFIDENCE_LEVEL) /
    (orderedPairCount * PAIRED_CONFIDENCE_LOOK_COUNT);
  const familyCriticalValue = exact || truncated
    ? 0
    : studentTQuantile(1 - perComparisonAlpha, evaluatedWorldCount - 1);
  const weighting = leader.worldWeights.every((weight) => weight === 1)
    ? 'uniform'
    : 'shared-world-weighted';

  const comparisons = ordered.slice(1).map((alternative) => {
    const centeredContributions = leader.contractUtilityOutcomes.map((leaderOutcome, index) => {
      const leaderWeight = leader.worldWeights[index]!;
      const alternativeWeight = alternative.worldWeights[index]!;
      if (
        leaderWeight !== alternativeWeight ||
        !Number.isFinite(leaderWeight) ||
        leaderWeight <= 0
      ) {
        throw new Error('Paired confidence requires one positive weight for each shared world.');
      }
      return leaderWeight * (
        leaderOutcome - alternative.contractUtilityOutcomes[index]! - strategicTolerance
      );
    });
    const meanCenteredContribution =
      centeredContributions.reduce((sum, value) => sum + value, 0) /
      evaluatedWorldCount;
    const standardDeviation = evaluatedWorldCount > 1
      ? sampleStandardDeviation(centeredContributions)
      : 0;
    const standardError = standardDeviation / Math.sqrt(evaluatedWorldCount);
    const identified = exact || (!truncated && standardDeviation > 0);
    const criticalValue = identified ? familyCriticalValue : null;
    const testStatistic = !exact && identified
      ? meanCenteredContribution / standardError
      : null;
    const lowerBound = exact
      ? meanCenteredContribution
      : identified
        ? meanCenteredContribution - familyCriticalValue * standardError
        : null;
    return {
      alternativeActionId: actionToId(alternative.action.action),
      meanDifference: leader.contractUtility - alternative.contractUtility,
      strategicTolerance,
      meanCenteredContribution,
      standardError,
      criticalValue,
      testStatistic,
      lowerBound,
      separated: lowerBound !== null && lowerBound > 0,
    };
  });
  const unresolvedAlternatives = comparisons
    .filter((comparison) => !comparison.separated)
    .map((comparison) => comparison.alternativeActionId);
  const clearLeader = unresolvedAlternatives.length === 0;
  const unresolvedActionIds = clearLeader
    ? []
    : [leaderActionId, ...unresolvedAlternatives];

  return {
    method: exact
      ? 'exact-paired-enumeration'
      : truncated
        ? 'truncated-enumeration-unavailable'
        : 'paired-t-alpha-spending',
    confidenceLevel: exact || truncated ? null : PAIRED_CONFIDENCE_LEVEL,
    evaluatedWorldCount,
    plannedLookCount: exact || truncated ? 1 : PAIRED_CONFIDENCE_LOOK_COUNT,
    weighting,
    assumption: exact
      ? 'exhaustive-valid-worlds'
      : truncated
        ? 'truncated-world-prefix'
        : 'iid-worlds-and-normal-paired-mean',
    lowerBoundUnits: 'weighted-contract-utility-contribution',
    informationSet: 'filtered-seat-state-and-legal-actions',
    worldOrder: 'shared-ordered',
    leaderActionId,
    clearLeader,
    unresolvedActionIds,
    unresolvedCandidateCount: unresolvedActionIds.length,
    comparisons,
  };
}
